/**
 * Settings -> Integrations -> Applications: connect an outside application so PeopleOrbit creates
 * each employee there and, when chosen, keeps their details current (ADR 0033).
 *
 * NOT the calendar/Slack connections: those are `integrations.contract.ts` under the
 * `integration:*` permissions. These routes use `app_integration:*`, which every company assigns
 * to whichever roles it wants in Settings -> Roles (nothing here names a role).
 *
 * THE SHAPE OF IT
 *   - An application is either a SIMPLE WEBHOOK (we POST our documented standard payload, below) or
 *     a CUSTOM API (HR writes, per operation, the method, path and JSON body the application wants).
 *   - Operations are `create`, `update` and `lookup`. There is no delete or deactivate: accounts
 *     in other applications are never removed by PeopleOrbit (owner decision 2026-10-10).
 *   - Secrets (token, password, client secret, secret headers, the signing secret) are WRITE-ONLY:
 *     a read says whether one is set, never what it is. Omit one on update to keep it.
 *   - Work happens after the employee is saved, in the background; these routes configure it,
 *     show it, and start batches. Batch starts take an `Idempotency-Key` header (invariant 10).
 *   - Routes that return request/reply details are hidden from the AI copilot (`ai: 'hidden'`):
 *     those details carry employees' names and emails as sent to a third party.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  dateOnlySchema,
  employmentTypeSchema,
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';
import {
  APP_INTEGRATION_AUTH_TYPES,
  APP_INTEGRATION_BATCH_KINDS,
  APP_INTEGRATION_BATCH_STATUSES,
  APP_INTEGRATION_EMPLOYEE_FIELDS,
  APP_INTEGRATION_INPUT_TYPES,
  APP_INTEGRATION_JOB_STATUSES,
  APP_INTEGRATION_JOB_TRIGGERS,
  APP_INTEGRATION_KINDS,
  APP_INTEGRATION_LOOKUP_METHODS,
  APP_INTEGRATION_MAX_HEADERS,
  APP_INTEGRATION_MAX_INPUTS,
  APP_INTEGRATION_MODES,
  APP_INTEGRATION_OPERATION_KINDS,
  APP_INTEGRATION_PRESENCE,
  APP_INTEGRATION_RATE_DEFAULT,
  APP_INTEGRATION_RATE_MAX,
  APP_INTEGRATION_RATE_MIN,
  APP_INTEGRATION_REPLY_PATH,
  APP_INTEGRATION_SEND_TIMINGS,
  APP_INTEGRATION_SKIP_REASONS,
  APP_INTEGRATION_SYNC_STATUSES,
  APP_INTEGRATION_TEMPLATE_MAX_CHARS,
  APP_INTEGRATION_WRITE_METHODS,
  appIntegrationHeaderProblem,
  appIntegrationPathProblem,
  appIntegrationTemplateProblems,
  appIntegrationTemplateSize,
  appIntegrationUrlProblem,
} from '../lib/app-integrations.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

const keySchema = z
  .string()
  .trim()
  .regex(
    /^[a-z][a-z0-9_]{0,59}$/,
    'Lowercase letters, numbers and underscores, starting with a letter',
  );

export const appIntegrationUrlSchema = z
  .string()
  .trim()
  .superRefine((value, ctx) => {
    const problem = appIntegrationUrlProblem(value);
    if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem });
  });

const pathSchema = z
  .string()
  .trim()
  .superRefine((value, ctx) => {
    const problem = appIntegrationPathProblem(value);
    if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem });
  });

const statusCodeSchema = z.number().int().min(100).max(599);
const replyPathSchema = z
  .string()
  .trim()
  .max(200)
  .regex(APP_INTEGRATION_REPLY_PATH, 'Use a dotted path such as data.id');

/** "The reply means success / already exists when `path` equals `equals`." */
export const appIntegrationBodyRuleSchema = z.object({
  path: replyPathSchema.min(1),
  equals: z.union([z.string().max(200), z.number(), z.boolean(), z.null()]),
});
export type AppIntegrationBodyRule = z.infer<typeof appIntegrationBodyRuleSchema>;

/** A JSON object, the body (or query) of a request. Size and placeholders are checked separately. */
const jsonObjectSchema = z.record(z.string(), z.unknown());

/** A question HR answers per employee, used in templates as `{{input.<key>}}`. */
export const appIntegrationInputSchema = z
  .object({
    key: keySchema,
    label: z.string().trim().min(1).max(200),
    type: z.enum(APP_INTEGRATION_INPUT_TYPES),
    options: z.array(z.string().trim().min(1).max(200)).min(1).max(50).nullable().default(null),
    required: z.boolean().default(false),
    /** Used where nobody can be asked: bulk import, offer hand-off, "Show in Add Employee" off. */
    defaultValue: z.string().max(500).nullable().default(null),
  })
  .refine((v) => v.type !== 'dropdown' || (v.options && v.options.length > 0), {
    message: 'A dropdown question needs at least one option',
    path: ['options'],
  });
export type AppIntegrationInput = z.infer<typeof appIntegrationInputSchema>;

/** Who the application is for. Every list empty = everyone. */
export const appIntegrationAudienceSchema = z.object({
  legalEntityIds: z.array(uuidSchema).max(200).default([]),
  locationIds: z.array(uuidSchema).max(500).default([]),
  departmentIds: z.array(uuidSchema).max(500).default([]),
  /** Employment type codes (`full_time`, `contract`, …). */
  employmentTypes: z.array(employmentTypeSchema).max(20).default([]),
});
export type AppIntegrationAudience = z.infer<typeof appIntegrationAudienceSchema>;

// ---------------------------------------------------------------------------
// Login (write: secrets optional = keep; read: whether set)
// ---------------------------------------------------------------------------

const secretInput = z.string().min(1).max(4000);

export const appIntegrationAuthInputSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('none') }),
  z.object({ type: z.literal('bearer'), token: secretInput.optional() }),
  z.object({
    type: z.literal('basic'),
    username: z.string().trim().min(1).max(200),
    password: secretInput.optional(),
  }),
  z.object({
    type: z.literal('api_key'),
    headerName: z
      .string()
      .trim()
      .superRefine((value, ctx) => {
        const problem = appIntegrationHeaderProblem(value);
        if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem });
      }),
    key: secretInput.optional(),
  }),
  z.object({
    type: z.literal('oauth2_client_credentials'),
    /** Checked by the same rules as the base address and vetted the same way by the server (N9). */
    tokenUrl: appIntegrationUrlSchema,
    clientId: z.string().trim().min(1).max(500),
    clientSecret: secretInput.optional(),
    scope: z.string().trim().max(500).nullable().default(null),
  }),
]);
export type AppIntegrationAuthInput = z.infer<typeof appIntegrationAuthInputSchema>;

export const appIntegrationAuthSchema = z.object({
  type: z.enum(APP_INTEGRATION_AUTH_TYPES),
  username: z.string().nullable(),
  headerName: z.string().nullable(),
  tokenUrl: z.string().nullable(),
  clientId: z.string().nullable(),
  scope: z.string().nullable(),
  /** True when the token / password / key / client secret is stored. Never the value. */
  secretSet: z.boolean(),
});
export type AppIntegrationAuth = z.infer<typeof appIntegrationAuthSchema>;

/** An extra header. A secret one is stored encrypted and never shown again (N6). */
export const appIntegrationHeaderInputSchema = z.object({
  name: z
    .string()
    .trim()
    .superRefine((value, ctx) => {
      const problem = appIntegrationHeaderProblem(value);
      if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem });
    }),
  /** Omit on update to keep a stored secret value. */
  value: z.string().max(4000).optional(),
  secret: z.boolean(),
});

export const appIntegrationHeaderSchema = z.object({
  name: z.string(),
  secret: z.boolean(),
  /** The value for a plain header; null for a secret one. */
  value: z.string().nullable(),
  valueSet: z.boolean(),
});

// ---------------------------------------------------------------------------
// Operations (custom applications)
// ---------------------------------------------------------------------------

/**
 * One request. In an UPDATE's path and query, `{{employee.workEmail}}` / `{{employee.employeeCode}}`
 * resolve to the value LAST SENT to the application, so a corrected email still reaches the same
 * account (N4); in bodies they are the current value. `{{externalId}}` is the application's own id.
 */
export const appIntegrationOperationInputSchema = z
  .object({
    kind: z.enum(APP_INTEGRATION_OPERATION_KINDS),
    enabled: z.boolean().default(true),
    method: z.enum([...APP_INTEGRATION_WRITE_METHODS, ...APP_INTEGRATION_LOOKUP_METHODS]),
    path: pathSchema,
    /** Query parameters; values may hold placeholders. */
    query: z.record(z.string().min(1).max(100), z.string().max(1000)).default({}),
    /** JSON body for POST/PUT/PATCH; null for a GET lookup. */
    body: jsonObjectSchema.nullable().default(null),
    successStatuses: z.array(statusCodeSchema).min(1).max(10).default([200, 201, 202, 204]),
    /** Optional: the reply is a success only when this also holds (`200 {"success": false}` is not). */
    successBodyRule: appIntegrationBodyRuleSchema.nullable().default(null),
    /** Create: replies meaning "this person already exists" — skipped, not failed. */
    existsStatuses: z.array(statusCodeSchema).max(10).default([409]),
    existsBodyRule: appIntegrationBodyRuleSchema.nullable().default(null),
    /** Update: replies meaning "no such person" — shown to HR, never retried. */
    notFoundStatuses: z.array(statusCodeSchema).max(10).default([404]),
    /** Where the application's id for the person sits in a create (or lookup match) reply. */
    externalIdPath: replyPathSchema.nullable().default(null),
    /** Lookup: where the list of matches sits in the reply ('' = the reply itself). */
    resultsPath: replyPathSchema.nullable().default(null),
  })
  .superRefine((op, ctx) => {
    const writes = (APP_INTEGRATION_WRITE_METHODS as readonly string[]).includes(op.method);
    if (op.kind === 'lookup' && writes && op.method !== 'POST') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A lookup uses GET or POST',
        path: ['method'],
      });
    }
    if (op.kind !== 'lookup' && !writes) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Use POST, PUT or PATCH',
        path: ['method'],
      });
    }
    if (op.method === 'GET' && op.body !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A GET request has no body',
        path: ['body'],
      });
    }
    if (
      appIntegrationTemplateSize(op.body) + appIntegrationTemplateSize(op.query) >
      APP_INTEGRATION_TEMPLATE_MAX_CHARS
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `The request template is longer than ${APP_INTEGRATION_TEMPLATE_MAX_CHARS} characters`,
        path: ['body'],
      });
    }
  });
export type AppIntegrationOperationInput = z.infer<typeof appIntegrationOperationInputSchema>;

export const appIntegrationOperationSchema = z.object({
  kind: z.enum(APP_INTEGRATION_OPERATION_KINDS),
  enabled: z.boolean(),
  method: z.string(),
  path: z.string(),
  query: z.record(z.string(), z.string()),
  body: jsonObjectSchema.nullable(),
  successStatuses: z.array(z.number().int()),
  successBodyRule: appIntegrationBodyRuleSchema.nullable(),
  existsStatuses: z.array(z.number().int()),
  existsBodyRule: appIntegrationBodyRuleSchema.nullable(),
  notFoundStatuses: z.array(z.number().int()),
  externalIdPath: z.string().nullable(),
  resultsPath: z.string().nullable(),
});
export type AppIntegrationOperation = z.infer<typeof appIntegrationOperationSchema>;

// ---------------------------------------------------------------------------
// The application
// ---------------------------------------------------------------------------

const applicationFields = {
  name: z.string().trim().min(1).max(100),
  baseUrl: appIntegrationUrlSchema,
  auth: appIntegrationAuthInputSchema,
  headers: z.array(appIntegrationHeaderInputSchema).max(APP_INTEGRATION_MAX_HEADERS).default([]),
  /** Sign every request with HMAC-SHA256 (`X-PeopleOrbit-Signature`). Simple webhooks: recommended. */
  signRequests: z.boolean().default(false),
  /** Send `Idempotency-Key` (on by default; off only for APIs that reject unknown headers). */
  sendIdempotencyKey: z.boolean().default(true),
  /** "Add and update" when true, "Add only" when false (K2). */
  updateEnabled: z.boolean().default(true),
  sendTiming: z.enum(APP_INTEGRATION_SEND_TIMINGS).default('on_joining_date'),
  audience: appIntegrationAudienceSchema.default({}),
  inputs: z.array(appIntegrationInputSchema).max(APP_INTEGRATION_MAX_INPUTS).default([]),
  /** Show this application on Add Employee's "Applications" step. */
  showInAddEmployee: z.boolean().default(true),
  ratePerSecond: z
    .number()
    .int()
    .min(APP_INTEGRATION_RATE_MIN)
    .max(APP_INTEGRATION_RATE_MAX)
    .default(APP_INTEGRATION_RATE_DEFAULT),
  /** Custom applications only: create is required, update when `updateEnabled`, lookup optional. */
  operations: z.array(appIntegrationOperationInputSchema).max(3).default([]),
};

function checkApplication(
  app: {
    kind?: (typeof APP_INTEGRATION_KINDS)[number];
    inputs?: AppIntegrationInput[];
    operations?: AppIntegrationOperationInput[];
    headers?: z.infer<typeof appIntegrationHeaderInputSchema>[];
    showInAddEmployee?: boolean;
    updateEnabled?: boolean;
    auth?: AppIntegrationAuthInput;
  },
  ctx: z.RefinementCtx,
  creating: boolean,
): void {
  // "Omit a secret to keep it" only means something once one is stored: a new application needs them.
  if (creating) {
    const auth = app.auth;
    const missingSecret =
      (auth?.type === 'bearer' && !auth.token) ||
      (auth?.type === 'basic' && !auth.password) ||
      (auth?.type === 'api_key' && !auth.key) ||
      (auth?.type === 'oauth2_client_credentials' && !auth.clientSecret);
    if (missingSecret) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Enter the secret for this login',
        path: ['auth'],
      });
    }
  }
  const inputs = app.inputs ?? [];
  const inputKeys = inputs.map((i) => i.key);
  if (new Set(inputKeys).size !== inputKeys.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Two questions have the same key',
      path: ['inputs'],
    });
  }
  const headerNames = (app.headers ?? []).map((h) => h.name.toLowerCase());
  if (new Set(headerNames).size !== headerNames.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Two headers have the same name',
      path: ['headers'],
    });
  }
  for (const [i, h] of (app.headers ?? []).entries()) {
    if ((creating || !h.secret) && (h.value === undefined || h.value.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Enter a value',
        path: ['headers', i, 'value'],
      });
    }
  }
  if (
    app.showInAddEmployee === false &&
    inputs.some((i) => i.required && i.defaultValue === null)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'A required question has no default answer, so it must be asked on Add Employee',
      path: ['showInAddEmployee'],
    });
  }
  if (app.operations === undefined) return;
  const kinds = app.operations.map((o) => o.kind);
  if (new Set(kinds).size !== kinds.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Each operation can appear once',
      path: ['operations'],
    });
  }
  if (app.kind === 'simple_webhook' && app.operations.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'A simple webhook sends the standard payload; it has no operations',
      path: ['operations'],
    });
  }
  if (app.kind === 'custom') {
    if (!kinds.includes('create')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Add the create operation',
        path: ['operations'],
      });
    }
    if (app.updateEnabled && !kinds.includes('update')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '"Add and update" needs an update operation',
        path: ['operations'],
      });
    }
  }
  for (const [i, op] of app.operations.entries()) {
    const problems = [
      ...appIntegrationTemplateProblems(op.path, 'path', op.kind, inputKeys),
      ...appIntegrationTemplateProblems(op.query, 'query', op.kind, inputKeys),
      ...appIntegrationTemplateProblems(op.body, 'body', op.kind, inputKeys),
    ];
    for (const p of problems) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${p.placeholder}: ${p.message}`,
        path: ['operations', i],
      });
    }
  }
}

export const createAppIntegrationBodySchema = z
  .object({ kind: z.enum(APP_INTEGRATION_KINDS), ...applicationFields })
  .superRefine((app, ctx) => checkApplication(app, ctx, true));
export type CreateAppIntegrationBody = z.infer<typeof createAppIntegrationBodySchema>;

/** The kind is fixed once created. Every field optional; secrets omitted are kept. */
export const updateAppIntegrationBodySchema = z
  .object({
    name: applicationFields.name.optional(),
    baseUrl: appIntegrationUrlSchema.optional(),
    auth: appIntegrationAuthInputSchema.optional(),
    headers: z.array(appIntegrationHeaderInputSchema).max(APP_INTEGRATION_MAX_HEADERS).optional(),
    signRequests: z.boolean().optional(),
    sendIdempotencyKey: z.boolean().optional(),
    updateEnabled: z.boolean().optional(),
    sendTiming: z.enum(APP_INTEGRATION_SEND_TIMINGS).optional(),
    audience: appIntegrationAudienceSchema.optional(),
    inputs: z.array(appIntegrationInputSchema).max(APP_INTEGRATION_MAX_INPUTS).optional(),
    showInAddEmployee: z.boolean().optional(),
    ratePerSecond: z
      .number()
      .int()
      .min(APP_INTEGRATION_RATE_MIN)
      .max(APP_INTEGRATION_RATE_MAX)
      .optional(),
    operations: z.array(appIntegrationOperationInputSchema).max(3).optional(),
  })
  .superRefine((app, ctx) => checkApplication(app, ctx, false));
export type UpdateAppIntegrationBody = z.infer<typeof updateAppIntegrationBodySchema>;

export const appIntegrationCountsSchema = z.object({
  synced: z.number().int().nonnegative(),
  scheduled: z.number().int().nonnegative(),
  waiting: z.number().int().nonnegative(),
  needsInput: z.number().int().nonnegative(),
  ambiguous: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  outOfDate: z.number().int().nonnegative(),
});

export const appIntegrationSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  name: z.string(),
  kind: z.enum(APP_INTEGRATION_KINDS),
  mode: z.enum(APP_INTEGRATION_MODES),
  /** Off: nothing is recorded or sent. On + mode decides the rest. */
  isActive: z.boolean(),
  archivedAt: instantSchema.nullable(),
  baseUrl: z.string(),
  auth: appIntegrationAuthSchema,
  headers: z.array(appIntegrationHeaderSchema),
  signRequests: z.boolean(),
  signingSecretSet: z.boolean(),
  secretRotatedAt: instantSchema.nullable(),
  sendIdempotencyKey: z.boolean(),
  updateEnabled: z.boolean(),
  sendTiming: z.enum(APP_INTEGRATION_SEND_TIMINGS),
  audience: appIntegrationAudienceSchema,
  inputs: z.array(appIntegrationInputSchema),
  showInAddEmployee: z.boolean(),
  ratePerSecond: z.number().int(),
  operations: z.array(appIntegrationOperationSchema),
  templateVersion: z.number().int(),
  dryRunSince: instantSchema.nullable(),
  counts: appIntegrationCountsSchema,
  lastSyncAt: instantSchema.nullable(),
  /** Another active application using the same address and create path (double creation). */
  sameAddressAs: z.array(z.object({ id: uuidSchema, name: z.string() })),
  /** Shown on the setup page for the application's allow-list (from configuration). */
  outboundIpAddresses: z.array(z.string()),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type AppIntegration = z.infer<typeof appIntegrationSchema>;

/** Create / rotate return the signing secret ONCE, here; it is never readable again. */
export const appIntegrationWithSecretSchema = z.object({
  application: appIntegrationSchema,
  signingSecret: z.string().nullable(),
});

export const changeAppIntegrationStateBodySchema = z.object({
  action: z.enum(['go_live', 'dry_run', 'pause', 'resume', 'turn_off', 'turn_on']),
});

export const appIntegrationStateResultSchema = z.object({
  application: appIntegrationSchema,
  /** People whose sends waited (dry run / paused); offer "send them now" via a `send_waiting` batch. */
  waitingCount: z.number().int().nonnegative(),
  /** Scheduled and waiting jobs cancelled by turning the application off or archiving it. */
  cancelledCount: z.number().int().nonnegative(),
});

// ---------------------------------------------------------------------------
// Check connection / test send
// ---------------------------------------------------------------------------

/** What was sent and what came back, secrets masked, at most ~1 KB of each body. */
export const appIntegrationExchangeSchema = z.object({
  request: z.object({
    method: z.string(),
    url: z.string(),
    headers: z.record(z.string(), z.string()),
    body: z.string().nullable(),
  }),
  response: z
    .object({
      status: z.number().int(),
      durationMs: z.number().int().nonnegative(),
      body: z.string().nullable(),
    })
    .nullable(),
});

export const appIntegrationCheckResultSchema = z.object({
  ok: z.boolean(),
  /** Plain-language outcome: "Connected", "The login was refused (401)", "The address redirects to …". */
  message: z.string(),
  exchange: appIntegrationExchangeSchema.nullable(),
  checkedAt: instantSchema,
});
export type AppIntegrationCheckResult = z.infer<typeof appIntegrationCheckResultSchema>;

/**
 * Send test: a made-up employee (never a real one). HR may change the name and email so the record
 * is easy to recognise in the application. It is a real record there, and PeopleOrbit never removes it.
 */
export const testAppIntegrationBodySchema = z.object({
  firstName: z.string().trim().min(1).max(100).default('Test'),
  lastName: z.string().trim().min(1).max(100).default('Employee (PeopleOrbit)'),
  workEmail: z.string().trim().email().max(320).optional(),
  answers: z.record(z.string(), z.string().max(500)).default({}),
});

// ---------------------------------------------------------------------------
// Per-employee status
// ---------------------------------------------------------------------------

export const appIntegrationEmployeeStatusSchema = z.object({
  applicationId: uuidSchema,
  applicationName: z.string(),
  status: z.enum(APP_INTEGRATION_SYNC_STATUSES),
  presence: z.enum(APP_INTEGRATION_PRESENCE),
  externalId: z.string().nullable(),
  linkSource: z.enum(['created', 'lookup', 'manual']).nullable(),
  skipReason: z.enum(APP_INTEGRATION_SKIP_REASONS).nullable(),
  /** The day a scheduled create goes out (joining date, location time). */
  scheduledFor: instantSchema.nullable(),
  lastSyncAt: instantSchema.nullable(),
  /** Plain-language reason for failed / needs input / ambiguous / no remote key. */
  message: z.string().nullable(),
  /** False when updates cannot be addressed: no remote id and no key was ever sent. */
  canUpdate: z.boolean(),
  answers: z.record(z.string(), z.string().nullable()),
});
export type AppIntegrationEmployeeStatus = z.infer<typeof appIntegrationEmployeeStatusSchema>;

export const appIntegrationEmployeeStatusesSchema = z.object({
  employmentId: uuidSchema,
  applications: z.array(appIntegrationEmployeeStatusSchema),
});

export const listAppIntegrationStatusesQuerySchema = z.object({
  /** The employment ids on the current list page (comma-separated, at most 100). */
  employmentIds: z
    .string()
    .transform((value) => value.split(',').filter((s) => s.length > 0))
    .pipe(z.array(uuidSchema).min(1).max(100)),
});

export const editAppIntegrationAnswersBodySchema = z.object({
  answers: z.record(keySchema, z.string().max(500).nullable()),
});

export const linkAppIntegrationBodySchema = z.object({
  /** The application's id for this person, as it appears in the application. */
  externalId: z.string().trim().min(1).max(500),
});

export const bulkLinkAppIntegrationBodySchema = z.object({
  rows: z
    .array(
      z
        .object({
          employeeCode: z.string().trim().min(1).max(50).optional(),
          workEmail: z.string().trim().email().max(320).optional(),
          externalId: z.string().trim().min(1).max(500),
        })
        .refine((r) => r.employeeCode !== undefined || r.workEmail !== undefined, {
          message: 'Give the employee code or the work email',
        }),
    )
    .min(1)
    .max(1000),
});

export const bulkLinkResultSchema = z.object({
  linked: z.number().int().nonnegative(),
  problems: z.array(z.object({ row: z.number().int(), message: z.string() })),
});

/** Applications to show on Add Employee's step 4 for a new hire with these placements. */
export const appIntegrationsForNewEmployeeQuerySchema = z.object({
  legalEntityId: uuidSchema,
  locationId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
  employmentType: employmentTypeSchema.optional(),
  joiningDate: dateOnlySchema.optional(),
});

export const appIntegrationForNewEmployeeSchema = z.object({
  applicationId: uuidSchema,
  name: z.string(),
  mode: z.enum(APP_INTEGRATION_MODES),
  sendTiming: z.enum(APP_INTEGRATION_SEND_TIMINGS),
  inputs: z.array(appIntegrationInputSchema),
});

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

export const appIntegrationJobSchema = z.object({
  id: uuidSchema,
  applicationId: uuidSchema,
  employmentId: uuidSchema.nullable(),
  employeeName: z.string().nullable(),
  employeeCode: z.string().nullable(),
  kind: z.enum(APP_INTEGRATION_OPERATION_KINDS),
  trigger: z.enum(APP_INTEGRATION_JOB_TRIGGERS),
  status: z.enum(APP_INTEGRATION_JOB_STATUSES),
  skipReason: z.enum(APP_INTEGRATION_SKIP_REASONS).nullable(),
  attempts: z.number().int().nonnegative(),
  httpStatus: z.number().int().nullable(),
  errorCode: z.string().nullable(),
  scheduledFor: instantSchema.nullable(),
  batchId: uuidSchema.nullable(),
  startedAt: instantSchema.nullable(),
  finishedAt: instantSchema.nullable(),
  createdAt: instantSchema,
});
export type AppIntegrationJob = z.infer<typeof appIntegrationJobSchema>;

export const appIntegrationJobDetailSchema = appIntegrationJobSchema.extend({
  /** Kept for failed jobs and dry runs only, for 30 days; null afterwards ("details expired"). */
  exchange: appIntegrationExchangeSchema.nullable(),
  detailsExpired: z.boolean(),
});

export const listAppIntegrationJobsQuerySchema = paginationQuerySchema.extend({
  status: z.enum(APP_INTEGRATION_JOB_STATUSES).optional(),
  employmentId: uuidSchema.optional(),
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
});

export const appIntegrationHealthSchema = z.object({
  applicationId: uuidSchema,
  mode: z.enum(APP_INTEGRATION_MODES),
  last7Days: z.object({
    sent: z.number().int().nonnegative(),
    succeeded: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    /** null when nothing was sent. */
    successRate: z.number().min(0).max(1).nullable(),
    p95LatencyMs: z.number().int().nonnegative().nullable(),
  }),
  breaker: z.object({
    open: z.boolean(),
    consecutiveFailures: z.number().int().nonnegative(),
    openedAt: instantSchema.nullable(),
  }),
  lastError: z.object({ at: instantSchema, message: z.string() }).nullable(),
});

// ---------------------------------------------------------------------------
// Batches: backfill, resync, send-waiting
// ---------------------------------------------------------------------------

export const createAppIntegrationBatchBodySchema = z.object({
  kind: z.enum(APP_INTEGRATION_BATCH_KINDS),
  /** `selected`: the ids given; `all`: every eligible employee; `out_of_date`: resync only. */
  who: z.enum(['all', 'selected', 'out_of_date']),
  employmentIds: z.array(uuidSchema).max(5000).optional(),
  /** Employees serving notice are left out unless HR ticks this (decision G21). */
  includeNoticePeriod: z.boolean().default(false),
});

export const appIntegrationBatchBlockedSchema = z.object({
  employmentId: uuidSchema,
  name: z.string(),
  employeeCode: z.string(),
  reason: z.string(),
});

export const appIntegrationBatchSchema = z.object({
  id: uuidSchema,
  applicationId: uuidSchema,
  kind: z.enum(APP_INTEGRATION_BATCH_KINDS),
  status: z.enum(APP_INTEGRATION_BATCH_STATUSES),
  /** Background counting progress for large companies (N21), 0..1. */
  countingProgress: z.number().min(0).max(1),
  counts: z.object({
    willSendNow: z.number().int().nonnegative(),
    scheduledForJoiningDate: z.number().int().nonnegative(),
    alreadyPresent: z.number().int().nonnegative(),
    excluded: z.number().int().nonnegative(),
    blocked: z.number().int().nonnegative(),
    synced: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
  }),
  /** The first 50 who cannot be sent and why. */
  blocked: z.array(appIntegrationBatchBlockedSchema),
  /** One rendered request, secrets masked, so HR sees what will go. */
  sample: appIntegrationExchangeSchema.shape.request.nullable(),
  trialSentAt: instantSchema.nullable(),
  createdByName: z.string(),
  createdAt: instantSchema,
  finishedAt: instantSchema.nullable(),
});
export type AppIntegrationBatch = z.infer<typeof appIntegrationBatchSchema>;

export const appIntegrationBatchAnswersBodySchema = z.object({
  /** One answer for everyone. */
  defaults: z.record(keySchema, z.string().max(500)).default({}),
  /** Per-person answers (from the CSV), overriding the defaults. */
  rows: z
    .array(
      z.object({ employmentId: uuidSchema, answers: z.record(keySchema, z.string().max(500)) }),
    )
    .max(5000)
    .default([]),
});

export const idempotencyHeadersSchema = z.object({ 'idempotency-key': idempotencyKeySchema });

export const appIntegrationFailuresCsvSchema = z.object({
  filename: z.string(),
  contentType: z.literal('text/csv'),
  content: z.string(),
});

// ---------------------------------------------------------------------------
// The standard employee payload (simple webhooks), schema version 1
// ---------------------------------------------------------------------------

/**
 * What a simple webhook receives. Documented publicly; within version 1 fields are only ever ADDED
 * (and an addition never sends to anyone by itself — people are marked out of date instead).
 * Delivery is at-least-once: a receiver de-duplicates by `eventId`. `ping` is sent by Check connection.
 */
export const standardEmployeePayloadV1Schema = z.object({
  schemaVersion: z.literal(1),
  event: z.enum(['employee.created', 'employee.joined', 'employee.updated', 'ping']),
  eventId: z.string(),
  sentAt: instantSchema,
  company: z
    .object({ id: orgIdSchema, name: z.string(), legalEntity: z.string().nullable() })
    .optional(),
  employee: z
    .object({
      id: uuidSchema,
      employeeCode: z.string(),
      firstName: z.string(),
      lastName: z.string().nullable(),
      fullName: z.string(),
      workEmail: z.string().nullable(),
      personalEmail: z.string().nullable(),
      phone: z.string().nullable(),
      workPhone: z.string().nullable(),
      joiningDate: dateOnlySchema,
      /** Never notice period, leave or exit (N2). */
      status: z.enum(['pre_joining', 'active']),
      department: z.string().nullable(),
      designation: z.string().nullable(),
      location: z.string().nullable(),
      manager: z
        .object({
          name: z.string(),
          email: z.string().nullable(),
          employeeCode: z.string().nullable(),
        })
        .nullable(),
      /** Only custom fields the company marked "may be shared with applications". */
      customFields: z.record(z.string(), z.string().nullable()),
    })
    .optional(),
  answers: z.record(z.string(), z.string().nullable()).optional(),
});
export type StandardEmployeePayloadV1 = z.infer<typeof standardEmployeePayloadV1Schema>;

/** The employee fields of the standard payload, for the docs page and the form's field list. */
export const STANDARD_PAYLOAD_EMPLOYEE_FIELDS = APP_INTEGRATION_EMPLOYEE_FIELDS;

// ---------------------------------------------------------------------------
// Routes
// ---------------------------------------------------------------------------

const read = routeMeta('app_integration:read');
const manage = routeMeta('app_integration:manage');
const sync = routeMeta('app_integration:sync');
const readOrSync = routeMeta(['app_integration:read', 'app_integration:sync']);

const appParams = z.object({ id: uuidSchema });
const employeeParams = z.object({ id: uuidSchema, employmentId: uuidSchema });
const batchParams = z.object({ batchId: uuidSchema });

const errors = {
  401: errorBodySchema,
  403: errorBodySchema,
  404: errorBodySchema,
} as const;

export const appIntegrationsContract = c.router(
  {
    list: {
      method: 'GET',
      path: '/',
      // Not z.coerce.boolean(): it reads the string "false" as true.
      query: z.object({
        includeArchived: z
          .enum(['true', 'false'])
          .optional()
          .transform((v) => v === 'true'),
      }),
      responses: { 200: z.array(appIntegrationSchema), 401: errorBodySchema, 403: errorBodySchema },
      summary: "The company's connected applications",
      metadata: read,
    },
    // Static single-segment GET paths come before '/:id': Express matches in mounting order.
    forNewEmployee: {
      method: 'GET',
      path: '/for-new-employee',
      query: appIntegrationsForNewEmployeeQuerySchema,
      responses: {
        200: z.array(appIntegrationForNewEmployeeSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: "Applications for Add Employee's Applications step",
      metadata: sync,
    },
    listEmployeeStatuses: {
      method: 'GET',
      path: '/statuses',
      query: listAppIntegrationStatusesQuerySchema,
      responses: {
        200: z.array(appIntegrationEmployeeStatusesSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Status chips for the employees on one list page',
      metadata: readOrSync,
    },
    get: {
      method: 'GET',
      path: '/:id',
      pathParams: appParams,
      responses: { 200: appIntegrationSchema, ...errors },
      summary: 'One application, secrets reported as set or not, never shown',
      metadata: read,
    },
    create: {
      method: 'POST',
      path: '/',
      body: createAppIntegrationBodySchema,
      responses: {
        201: appIntegrationWithSecretSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Connect an application (starts in dry run; returns the signing secret once)',
      metadata: manage,
    },
    update: {
      method: 'PATCH',
      path: '/:id',
      pathParams: appParams,
      body: updateAppIntegrationBodySchema,
      responses: {
        200: appIntegrationSchema,
        ...errors,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Change an application; a changed template marks people out of date, it sends nothing',
      metadata: manage,
    },
    changeState: {
      method: 'POST',
      path: '/:id/state',
      pathParams: appParams,
      body: changeAppIntegrationStateBodySchema,
      responses: { 200: appIntegrationStateResultSchema, ...errors, 409: errorBodySchema },
      summary: 'Go live, dry run, pause, resume, turn off or on',
      metadata: manage,
    },
    archive: {
      method: 'POST',
      path: '/:id/archive',
      pathParams: appParams,
      body: c.noBody(),
      responses: { 200: appIntegrationStateResultSchema, ...errors },
      summary: 'Archive (history kept, sending stops, scheduled work cancelled)',
      metadata: manage,
    },
    duplicate: {
      method: 'POST',
      path: '/:id/duplicate',
      pathParams: appParams,
      body: z.object({ name: z.string().trim().min(1).max(100) }),
      responses: { 201: appIntegrationSchema, ...errors },
      summary: 'Copy an application without its secrets (starts in dry run)',
      metadata: manage,
    },
    rotateSigningSecret: {
      method: 'POST',
      path: '/:id/signing-secret',
      pathParams: appParams,
      body: c.noBody(),
      responses: { 200: appIntegrationWithSecretSchema, ...errors },
      summary: 'Generate a new signing secret; shown once',
      metadata: manage,
    },
    checkConnection: {
      method: 'POST',
      path: '/:id/check',
      pathParams: appParams,
      body: c.noBody(),
      responses: { 200: appIntegrationCheckResultSchema, ...errors, 429: errorBodySchema },
      summary: 'Log in and look up (or ping) without creating anything',
      metadata: routeMeta('app_integration:manage', { ai: 'hidden' }),
    },
    sendTest: {
      method: 'POST',
      path: '/:id/test',
      pathParams: appParams,
      body: testAppIntegrationBodySchema,
      responses: { 200: appIntegrationCheckResultSchema, ...errors, 429: errorBodySchema },
      summary: 'Create a made-up test employee in the application (a real record there)',
      metadata: routeMeta('app_integration:manage', { ai: 'hidden' }),
    },
    health: {
      method: 'GET',
      path: '/:id/health',
      pathParams: appParams,
      responses: { 200: appIntegrationHealthSchema, ...errors },
      summary: 'Success rate, latency and breaker state over the last 7 days',
      metadata: read,
    },
    listJobs: {
      method: 'GET',
      path: '/:id/jobs',
      pathParams: appParams,
      query: listAppIntegrationJobsQuerySchema,
      responses: { 200: paginatedSchema(appIntegrationJobSchema), ...errors },
      summary: 'History of one application',
      metadata: read,
    },
    getJob: {
      method: 'GET',
      path: '/jobs/:jobId',
      pathParams: z.object({ jobId: uuidSchema }),
      responses: { 200: appIntegrationJobDetailSchema, ...errors },
      summary: 'One history entry with the masked request and reply',
      metadata: routeMeta('app_integration:read', { ai: 'hidden' }),
    },
    getEmployeeStatuses: {
      method: 'GET',
      path: '/employees/:employmentId',
      pathParams: z.object({ employmentId: uuidSchema }),
      responses: { 200: appIntegrationEmployeeStatusesSchema, ...errors },
      summary: "One employee's status in every application (profile card)",
      metadata: readOrSync,
    },
    syncEmployee: {
      method: 'POST',
      path: '/:id/employees/:employmentId/sync',
      pathParams: employeeParams,
      body: c.noBody(),
      responses: { 202: appIntegrationEmployeeStatusSchema, ...errors, 409: errorBodySchema },
      summary: 'Send (or retry) one employee now',
      metadata: sync,
    },
    editAnswers: {
      method: 'PUT',
      path: '/:id/employees/:employmentId/answers',
      pathParams: employeeParams,
      body: editAppIntegrationAnswersBodySchema,
      responses: { 200: appIntegrationEmployeeStatusSchema, ...errors, 422: errorBodySchema },
      summary: "Change HR's answers for one employee (sends an update when they changed)",
      metadata: sync,
    },
    link: {
      method: 'POST',
      path: '/:id/employees/:employmentId/link',
      pathParams: employeeParams,
      body: linkAppIntegrationBodySchema,
      responses: { 200: appIntegrationEmployeeStatusSchema, ...errors, 409: errorBodySchema },
      summary: 'Attach the account this person already has in the application (sends nothing)',
      metadata: sync,
    },
    unlink: {
      method: 'POST',
      path: '/:id/employees/:employmentId/unlink',
      pathParams: employeeParams,
      body: c.noBody(),
      responses: { 200: appIntegrationEmployeeStatusSchema, ...errors },
      summary: 'Forget the link (the account in the application is untouched)',
      metadata: sync,
    },
    bulkLink: {
      method: 'POST',
      path: '/:id/link',
      pathParams: appParams,
      body: bulkLinkAppIntegrationBodySchema,
      responses: { 200: bulkLinkResultSchema, ...errors, 422: errorBodySchema },
      summary: 'Attach existing accounts from a list (sends nothing)',
      metadata: routeMeta('app_integration:manage', { ai: 'hidden' }),
    },
    createBatch: {
      method: 'POST',
      path: '/:id/batches',
      pathParams: appParams,
      body: createAppIntegrationBatchBodySchema,
      responses: { 201: appIntegrationBatchSchema, ...errors, 409: errorBodySchema },
      summary: 'Start a backfill, resync or send-waiting preview (counts in the background)',
      metadata: manage,
    },
    listBatches: {
      method: 'GET',
      path: '/:id/batches',
      pathParams: appParams,
      responses: { 200: z.array(appIntegrationBatchSchema), ...errors },
      summary: 'The latest bulk sends of one application (an open one first)',
      metadata: read,
    },
    getBatch: {
      method: 'GET',
      path: '/batches/:batchId',
      pathParams: batchParams,
      responses: { 200: appIntegrationBatchSchema, ...errors },
      summary: 'A batch: counts, who cannot be sent and why, progress',
      metadata: read,
    },
    setBatchAnswers: {
      method: 'PUT',
      path: '/batches/:batchId/answers',
      pathParams: batchParams,
      body: appIntegrationBatchAnswersBodySchema,
      responses: {
        200: appIntegrationBatchSchema,
        ...errors,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Answers for the people in a batch (one default and/or per person)',
      metadata: routeMeta('app_integration:manage', { ai: 'hidden' }),
    },
    sendBatchTrial: {
      method: 'POST',
      path: '/batches/:batchId/trial',
      pathParams: batchParams,
      headers: idempotencyHeadersSchema,
      body: c.noBody(),
      responses: { 202: appIntegrationBatchSchema, ...errors, 409: errorBodySchema },
      summary: 'Send exactly one employee from the batch as a trial',
      metadata: manage,
    },
    runBatch: {
      method: 'POST',
      path: '/batches/:batchId/run',
      pathParams: batchParams,
      headers: idempotencyHeadersSchema,
      body: c.noBody(),
      responses: { 202: appIntegrationBatchSchema, ...errors, 409: errorBodySchema },
      summary: 'Send the rest of the batch in the background',
      metadata: manage,
    },
    stopBatch: {
      method: 'POST',
      path: '/batches/:batchId/stop',
      pathParams: batchParams,
      body: c.noBody(),
      responses: { 200: appIntegrationBatchSchema, ...errors },
      summary: 'Stop a running batch (requests already sent stay sent)',
      metadata: manage,
    },
    retryBatchFailures: {
      method: 'POST',
      path: '/batches/:batchId/retry-failed',
      pathParams: batchParams,
      headers: idempotencyHeadersSchema,
      body: c.noBody(),
      responses: { 202: appIntegrationBatchSchema, ...errors, 409: errorBodySchema },
      summary: 'Send again only the people who failed',
      metadata: manage,
    },
    batchFailuresCsv: {
      method: 'GET',
      path: '/batches/:batchId/failures.csv',
      pathParams: batchParams,
      responses: { 200: appIntegrationFailuresCsvSchema, ...errors },
      summary: 'Who failed and why, as CSV',
      metadata: routeMeta('app_integration:read', { ai: 'hidden' }),
    },
  },
  { pathPrefix: '/app-integrations' },
);
